import { useEffect, useRef } from 'react';

/** A Next ring the scene asked for. `id` tells two rings of the same length apart. */
export interface NextRing {
  id: number;
  durationMs: number;
}

export interface NextRingOptions {
  /** The ring running now, or null. */
  ring: NextRing | null;
  /** While true the ring holds where it is (the tutorial is paused). */
  paused: boolean;
  /** Called once, when the ring has closed. */
  onClosed: () => void;
}

/**
 * Runs the Next ring's clock: `onClosed` fires once the ring has run for its
 * whole length, not counting time spent paused. A new ring starts from full
 * length; a null ring stops it.
 */
export function useNextRing({ ring, paused, onClosed }: NextRingOptions): void {
  const onClosedRef = useRef(onClosed);
  onClosedRef.current = onClosed;
  const remainingMsRef = useRef(0);
  const ringId = ring?.id ?? null;
  const durationMs = ring?.durationMs ?? 0;

  // Declared first, so a new ring is reset after the old timer's clean-up ran.
  useEffect(() => {
    remainingMsRef.current = durationMs;
  }, [ringId, durationMs]);

  useEffect(() => {
    if (ringId === null || paused) return undefined;
    const startedAt = Date.now();
    const timer = setTimeout(() => {
      remainingMsRef.current = 0;
      onClosedRef.current();
    }, remainingMsRef.current);
    return () => {
      clearTimeout(timer);
      remainingMsRef.current = Math.max(0, remainingMsRef.current - (Date.now() - startedAt));
    };
  }, [ringId, paused]);
}
